/**
 * Where a paste was aimed.
 *
 * The batch issue page listens for `paste` on the DOCUMENT, because a paste
 * with nothing focused - somebody hovers a row and presses Ctrl+V - is
 * delivered to document.body, outside the page's own markup. That listener
 * then treated ANY text containing a newline as a spreadsheet and parsed it
 * into rows.
 *
 * A newline does not mean "table". A description with a line break in it has
 * one too, and pasting one into the Description box had its paste CANCELLED
 * and its text fed to the CSV parser - so the text never arrived (owner,
 * 2026-09-24). The comment over that branch said "so normal single-field
 * pasting still works", which is precisely what it prevented.
 *
 * So: a paste aimed at somewhere a person can TYPE belongs to that field, and
 * the browser should handle it. Only a paste aimed at the page itself is a
 * candidate for row parsing.
 *
 * Deliberately NOT also requiring a delimiter before intercepting: a column of
 * bare titles, one per line with no tab or comma, is a legitimate multi-row
 * paste that the legacy fixed-order path supports. Tightening on delimiters
 * would break it, so the aim is the whole test.
 */
const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

export function isEditableTarget(el) {
  let node = el;
  // Walk up: the event target inside a rich editor may be a child node.
  while (node && node.nodeType === 1) {
    if (EDITABLE_TAGS.has(node.tagName)) return true;
    if (node.isContentEditable) return true;
    node = node.parentElement;
  }
  return false;
}

/** True when the document-level handler should take the paste for row parsing. */
export function shouldInterceptPaste(text, target) {
  if (isEditableTarget(target)) return false;
  return /\n/.test(String(text || ''));
}

export default shouldInterceptPaste;
